import styled from 'styled-components';

import {SyntheticEvent, useState} from 'react';

import {isNil} from 'ramda';

import {CompositionPhotoLayer} from '@dms/api/shared';
import {thumbnailUrl} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {getCroppedPhotoStyle} from '../utils/getCroppedPhotoStyle';
import {toCssPx} from '../utils/toCssPx';

interface CroppedReferencePhotoProps extends TestIdProps {
  crop: CompositionPhotoLayer['crop'];
  fit: CompositionPhotoLayer['fit'];
  // The layer box in view px.
  width: number;
  height: number;
}

type Rect = ReturnType<typeof getCroppedPhotoStyle>['region'];

const EMPTY_RECT: Rect = {left: 0, top: 0, width: 0, height: 0};

const toCssRect = (rect: Rect) => ({
  left: toCssPx(rect.left),
  top: toCssPx(rect.top),
  width: toCssPx(rect.width),
  height: toCssPx(rect.height),
});

// Computed from the layer box and the photo size, so the geometry is an inline style.
const PhotoRegion = styled.div.attrs<{$rect: Rect}>((props) => ({
  style: toCssRect(props.$rect),
}))<{$rect: Rect}>`
  position: absolute;
  overflow: hidden;
`;

const RegionPhoto = styled.img.attrs<{$rect: Rect}>((props) => ({
  style: toCssRect(props.$rect),
}))<{$rect: Rect}>`
  position: absolute;
  max-width: none;
  pointer-events: none;
  user-select: none;
`;

/**
 * The reference photo of the editor in the vehicle photo slot, cropped and fitted the way the BE
 * renders the vehicle photo, so the slot shows the result of the crop and fit settings.
 */
export function CroppedReferencePhoto(props: CroppedReferencePhotoProps) {
  const [photoSize, setPhotoSize] = useState<{width: number; height: number} | null>(null);

  const style = isNil(photoSize)
    ? {region: EMPTY_RECT, photo: EMPTY_RECT}
    : getCroppedPhotoStyle(
        props.crop,
        props.fit,
        {width: props.width, height: props.height},
        photoSize
      );

  const handleLoad = (event: SyntheticEvent<HTMLImageElement>) =>
    setPhotoSize({
      width: event.currentTarget.naturalWidth,
      height: event.currentTarget.naturalHeight,
    });

  return (
    <PhotoRegion $rect={style.region} data-testid={suffixTestId('region', props)}>
      <RegionPhoto
        src={`/${thumbnailUrl}`}
        alt=""
        draggable={false}
        onLoad={handleLoad}
        $rect={style.photo}
      />
    </PhotoRegion>
  );
}
